import React, { useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  UploadCloud,
  FileSpreadsheet,
  DownloadCloud,
  Loader2,
  AlertCircle,
  ListX,
  Sparkles,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { importCoursesFromExcel } from "../../../api/elearning/courses/courses";
import { showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";
import { startCourseImportTour } from "./courseImportTour";

const downloadCourseImportTemplate = () => {
  const url = `${process.env.PUBLIC_URL || ""}/templates/course_template.csv`;
  // CSV tiếng Việt đôi khi Excel mở sai encoding (font lỗi). Thêm UTF-8 BOM để đảm bảo hiển thị đúng.
  fetch(url)
    .then((r) => r.text())
    .then((text) => {
      const bom = "\uFEFF";
      const blob = new Blob([bom + text], {
        type: "text/csv;charset=utf-8",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "mau_nhap_khoa_hoc.csv";
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    })
    .catch(() => {
      // Fallback: tải link trực tiếp nếu fetch thất bại
      const link = document.createElement("a");
      link.href = url;
      link.download = "mau_nhap_khoa_hoc.csv";
      link.click();
    });
};

const CourseImportModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  /** Kết quả nhập file: imported + errors[] (line, message) */
  const [importReport, setImportReport] = useState(null);

  const reset = () => {
    setFile(null);
    setLoading(false);
    setImportReport(null);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose?.();
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    const name = selected.name.toLowerCase();
    const ok = name.endsWith(".xlsx") || name.endsWith(".xls") || name.endsWith(".csv");
    if (!ok) {
      showErrorToast("Vui lòng chọn file Excel (.xlsx, .xls) hoặc .csv");
      return;
    }
    setFile(selected);
    setImportReport(null);
  };

  const handleSubmit = async () => {
    if (!file) {
      showErrorToast("Vui lòng chọn file để nhập");
      return;
    }
    setImportReport(null);
    setLoading(true);
    try {
      const data = await importCoursesFromExcel(file);
      const imported = Number(data?.imported) || 0;
      const errors = Array.isArray(data?.errors) ? data.errors : [];
      const summaryMsg =
        typeof data?.message === "string" ? data.message : "";

      setImportReport({
        imported,
        errors,
        message: summaryMsg,
      });

      const hasErrors = errors.length > 0;
      const fullSuccess = imported > 0 && !hasErrors;

      if (imported > 0) {
        onSuccess?.();
      }

      if (fullSuccess) {
        showSuccessToast(
          summaryMsg || `Đã nhập ${imported} khóa học.`
        );
        setLoading(false);
        reset();
        onClose?.();
        return;
      }

      if (imported > 0 && hasErrors) {
        showSuccessToast(
          `Đã nhập ${imported} khóa học. Còn ${errors.length} dòng lỗi — xem chi tiết bên dưới.`
        );
      } else if (hasErrors) {
        showErrorToast(
          summaryMsg ||
            `Không nhập được dòng nào. Có ${errors.length} lỗi — xem chi tiết bên dưới.`
        );
      } else if (summaryMsg) {
        showSuccessToast(summaryMsg);
      }
    } catch (err) {
      const apiMsg = err.response?.data?.message || err.response?.data?.error;
      const text = apiMsg || "Không thể nhập file. Kiểm tra định dạng và dữ liệu.";
      showErrorToast(text);
      setImportReport({
        imported: 0,
        errors: [{ line: "—", message: text }],
        message: "",
      });
    } finally {
      setLoading(false);
    }
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={handleClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            className="relative w-full max-w-xl max-h-[90vh] flex flex-col bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden text-left"
            data-tour="course-import-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-100 flex justify-between items-start bg-gray-50/80" data-tour="course-import-header">
              <div>
                <h2 className="text-xl font-black text-gray-800 flex items-center gap-2 uppercase tracking-tight">
                  <UploadCloud className="text-red-600 shrink-0" size={24} />
                  Nhập khóa học từ Excel
                </h2>
                <p className="text-xs text-gray-500 font-medium mt-1">
                  Tải file theo mẫu, điền dữ liệu rồi tải lên hệ thống.
                </p>
              </div>
              <div className="flex items-center gap-2" data-tour="course-import-guide-btn">
                <button
                  type="button"
                  onClick={() => window.setTimeout(() => startCourseImportTour(), 80)}
                  title="Hướng dẫn nhanh"
                  aria-label="Hướng dẫn nhanh"
                  className="group relative inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-red-200 bg-white text-red-700 shadow-sm transition-all hover:bg-red-50 hover:border-red-300"
                >
                  <Sparkles className="h-4.5 w-4.5" strokeWidth={2.2} />
                  <span className="pointer-events-none absolute bottom-full right-0 mb-2 rounded-lg border border-red-100 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 whitespace-nowrap opacity-0 translate-y-1 shadow-md transition-all group-hover:opacity-100 group-hover:translate-y-0">
                    Hướng dẫn nhanh
                  </span>
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="p-2 rounded-xl hover:bg-white border border-transparent hover:border-gray-200 text-gray-400 transition-all disabled:opacity-50"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-5 overflow-y-auto flex-1 min-h-0">
              <div className="border-2 border-dashed border-gray-200 rounded-2xl p-8 hover:border-red-300 hover:bg-red-50/20 transition-all relative text-center" data-tour="course-import-upload">
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                  onChange={handleFileChange}
                  disabled={loading}
                />
                <div className="w-14 h-14 bg-red-100 text-red-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <FileSpreadsheet size={28} />
                </div>
                <p className="text-sm font-bold text-gray-800">
                  {file ? file.name : "Chọn hoặc kéo thả file vào đây"}
                </p>
                <p className="text-[11px] text-gray-400 mt-1 font-medium">.xlsx, .xls hoặc .csv</p>
              </div>

              <button
                type="button"
                onClick={downloadCourseImportTemplate}
                data-tour="course-import-template"
                className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-red-100 bg-red-50/50 text-red-700 text-xs font-bold uppercase tracking-wide hover:bg-red-50 transition-all"
              >
                <DownloadCloud size={16} />
                Tải file mẫu
              </button>

              <div className="flex gap-3 p-4 bg-amber-50 border border-amber-100 rounded-2xl" data-tour="course-import-note">
                <AlertCircle className="text-amber-600 shrink-0" size={18} />
                <p className="text-[11px] text-amber-900 leading-relaxed font-medium">
                  Cột &quot;Tên môn học&quot; phải khớp môn đã có trên hệ thống. Trình độ dùng mã (vd: lop-1, beginner)
                  cách nhau bằng dấu phẩy. Cột trung tâm / trường học: Y hoặc N.
                </p>
              </div>

              {importReport && importReport.errors.length > 0 && (
                <div className="rounded-2xl border border-red-200 bg-red-50/60 overflow-hidden" data-tour="course-import-errors">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-red-100 bg-red-50">
                    <ListX className="text-red-600 shrink-0" size={18} />
                    <div>
                      <p className="text-xs font-black text-red-800 uppercase tracking-tight">
                        Chi tiết lỗi ({importReport.errors.length} dòng)
                      </p>
                      {importReport.imported > 0 && (
                        <p className="text-[11px] text-red-700 font-medium mt-0.5">
                          Đã nhập thành công {importReport.imported} khóa học; các dòng sau bị bỏ qua.
                        </p>
                      )}
                    </div>
                  </div>
                  <ul className="max-h-52 overflow-y-auto divide-y divide-red-100/80 text-left">
                    {importReport.errors.map((row, idx) => (
                      <li
                        key={`${row.line}-${idx}`}
                        className="px-4 py-2.5 text-[12px] text-red-900 leading-snug"
                      >
                        <span className="font-bold text-red-800 tabular-nums">
                          Dòng {row.line}
                        </span>
                        <span className="text-red-600 mx-1.5">·</span>
                        <span className="text-red-900/90">{row.message}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-end gap-3" data-tour="course-import-actions">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="px-5 py-2.5 rounded-xl text-sm font-bold text-gray-500 hover:bg-gray-200 transition-all disabled:opacity-50"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || !file}
                className="px-6 py-2.5 rounded-xl text-sm font-bold bg-red-600 text-white hover:bg-red-700 shadow-lg shadow-red-100 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : null}
                {loading ? "Đang xử lý…" : "Nhập dữ liệu"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default CourseImportModal;
